import {
  catchError,
  debounceTime,
  filter,
  from,
  groupBy,
  map,
  mergeMap,
  Observable,
  of,
  share,
  Subject,
  take,
} from 'rxjs';
import { InVivoValidatorResult } from '@/support/hotValidator.util';
import { ApiService } from '@/support/ApiService';

/**
 * RxJS-based request deduplication for validation API calls.
 *
 * ## Purpose
 * Prevents "request storms" when multiple validators call the API simultaneously with
 * identical parameters (e.g., 100 spreadsheet cells validating on load). This implementation
 * provides **deduplication only**, not caching - sequential requests will make new API calls.
 * Long-term caching is handled by HTTP cache headers (Cache-Control) set by the server.
 *
 * ## How It Works (Fan-In/Fan-Out Pattern with Debouncing)
 * 1. **Fan-In**: Multiple validation requests with identical parameters are pushed to
 *    #validationQueue$ and grouped by their cache key via `groupBy()`
 * 2. **Batching Window**: `debounceTime(50)` waits 50ms after the last request in a group
 *    to collect near-concurrent requests (e.g., cells rendering in quick succession)
 * 3. **Single API Call**: After the debounce period, only one API call is made per group
 * 4. **Fan-Out**: The API response is broadcast to all subscribers waiting for that cache key
 * 5. **Group Lifecycle**: Groups close after processing via `take(1)`, ensuring no client-side
 *    result caching. Subsequent requests create new groups and new API calls.
 *
 * ## Deduplication Scope
 * - ✅ **Concurrent requests** (same key, within 50ms window): 1 API call shared by all
 * - ✅ **Near-concurrent requests** (same key, < 50ms apart): Batched into 1 API call
 * - ❌ **Sequential requests** (same key, > 50ms after previous): New API call each time
 *
 * ## Example
 * ```typescript
 * // 100 cells validate within 50ms window with same options
 * const promises = Array(100).fill(null).map(() =>
 *   cache.validate(glossaryId, new Set(['option1', 'option2']))
 * );
 * await Promise.all(promises);
 * // Result: Only 1 API call made after 50ms debounce, all 100 cells receive the same result
 *
 * // 100ms later, another cell validates with same options (outside debounce window)
 * await cache.validate(glossaryId, new Set(['option1', 'option2']));
 * // Result: New API call (HTTP cache will prevent redundant network request)
 * ```
 *
 * @see https://rxjs.dev/api/operators/groupBy - For understanding the grouping behavior
 * @see https://rxjs.dev/api/operators/debounceTime - For understanding the batching window
 * @see https://rxjs.dev/api/operators/share - For understanding result multicasting without caching
 */
export class ValidationRequestCache {
  /**
   * Queue of validation requests. Requests are pushed here and then processed
   * by the #validationResults$ pipeline.
   * @private
   */
  #validationQueue$ = new Subject<{ key: string; metadataGlossaryId: number; values: Array<string> }>();

  /**
   * Observable stream that processes validation requests and emits results.
   *
   * Pipeline:
   * 1. `groupBy(key)` - Creates separate Observable for each unique cache key
   * 2. `debounceTime(50)` - Waits 50ms after last event to batch near-concurrent requests
   * 3. `take(1)` - Each group processes only the last request after debounce, ignores earlier duplicates
   * 4. `mergeMap` - Converts the request into an API call (Promise → Observable)
   * 5. `share()` - Multicasts results to active subscribers only (no buffering)
   *
   * Note: Groups close after `take(1)`, so subsequent requests with same key (after debounce
   * window) create new groups and trigger new API calls. `share()` provides pure deduplication
   * without client-side caching - sequential requests will make new API calls.
   * @private
   */
  #validationResults$: Observable<{ key: string; result: InVivoValidatorResult }> = this.#validationQueue$.pipe(
    groupBy(({ key }) => key),
    mergeMap((group$) =>
      group$.pipe(
        debounceTime(50),
        take(1),
        mergeMap(({ key, metadataGlossaryId, values }) =>
          from(this.validateFunction(metadataGlossaryId, values)).pipe(
            map((result) => ({ key, result })),
            catchError(() => of({ key, result: { valid: false } as InVivoValidatorResult }))
          )
        )
      )
    ),
    share()
  );

  /**
   * Creates a new ValidationRequestCache instance.
   *
   * @param validateFunction - Function that performs the actual validation API call.
   *   Takes metadataGlossaryId and array of option values, returns validation result.
   *   This function should handle its own error cases and return appropriate results.
   */
  constructor(
    private validateFunction: (metadataGlossaryId: number, values: Array<string>) => Promise<InVivoValidatorResult>
  ) {}

  /**
   * Validates a set of option values against a metadata glossary.
   *
   * This method deduplicates near-concurrent identical requests using the fan-in/fan-out
   * pattern with a 50ms debounce window:
   * - Multiple calls within 50ms with same parameters share a single API call
   * - Calls outside the 50ms window make new API calls
   *
   * The 50ms debounce adds imperceptible latency (< 1 frame at 60fps) but significantly
   * improves deduplication by catching requests from:
   * - Same React render cycle
   * - Sequential useEffect chains
   * - Async key generation delays
   * - Browser event loop timing variations
   *
   * @param metadataGlossaryId {number} - The metadata glossary ID to validate against
   * @param values {Set<string>} - Set of option values to validate
   * @returns {Promise<InVivoValidatorResult>} Promise resolving to validation result: { valid: true } or { valid: false, message: string }
   *
   * @example
   * ```typescript
   * // Multiple validations within debounce window (deduped into 1 API call)
   * const results = await Promise.all([
   *   cache.validate(123, new Set(['option1', 'option2'])),  // T=0ms
   *   cache.validate(123, new Set(['option1', 'option2'])),  // T=5ms
   *   cache.validate(123, new Set(['option1', 'option2'])),  // T=10ms
   * ]);
   * // API call triggered at T=60ms (50ms after last request)
   * // All 3 receive same result from single API call
   *
   * // Later validation with same params (new API call, but HTTP cache may serve it)
   * await new Promise(resolve => setTimeout(resolve, 100)); // Wait > 50ms
   * const result = await cache.validate(123, new Set(['option1', 'option2']));
   * // New API call triggered (outside debounce window)
   * ```
   */
  async validate(metadataGlossaryId: number, values: Set<string>): Promise<InVivoValidatorResult> {
    const { key, values: sortedValues } = await ValidationRequestCache.generateKey(metadataGlossaryId, values);

    return await new Promise((resolve, reject) => {
      this.#validationResults$
        .pipe(
          filter((result) => result.key === key),
          take(1),
          map(({ result }) => result)
        )
        .subscribe({ next: resolve, error: reject });

      this.#validationQueue$.next({ key, metadataGlossaryId, values: sortedValues });
    });
  }

  /**
   * Generates a deterministic SHA-1 hash cache key from validation parameters and returns sorted values.
   *
   * This method serves dual purposes:
   * 1. Generates a deterministic cache key for request deduplication
   * 2. Returns sorted values array to ensure consistent ordering for API calls
   *
   * The cache key must be:
   * - **Deterministic**: Same inputs always produce same key
   * - **Order-independent**: Set(['a','b']) and Set(['b','a']) produce identical key
   * - **Unique**: Different glossaries or options produce different keys
   * - **Collision-resistant**: SHA-1 provides sufficient entropy for this use case
   *
   * @param metadataGlossaryId {number} - The metadata glossary ID
   * @param values {Set<string>} - Set of option values (order doesn't matter, will be sorted internally)
   * @returns {Promise<{ key: string, values: Array<string> }>} Promise resolving to object with:
   *   - `key`: 40-character hex string (SHA-1 hash) for cache deduplication
   *   - `values`: Sorted array of option values for consistent API calls
   *
   * @example
   * ```typescript
   * const { key, values } = await ValidationRequestCache.generateKey(
   *   123,
   *   new Set(['b', 'a', 'c'])
   * );
   * console.log(key);    // "a1b2c3d4e5f6..." (40 hex characters)
   * console.log(values); // ['a', 'b', 'c'] (sorted)
   *
   * // Order-independent: same inputs in different order produce same key
   * const result1 = await ValidationRequestCache.generateKey(123, new Set(['a', 'b']));
   * const result2 = await ValidationRequestCache.generateKey(123, new Set(['b', 'a']));
   * console.assert(result1.key === result2.key); // true
   * ```
   */
  static async generateKey(
    metadataGlossaryId: number,
    values: Set<string>
  ): Promise<{ key: string; values: Array<string> }> {
    // Sort values to ensure order-independence for cache key generation
    const sortedValues = Array.from(values).sort();
    // Concatenate glossary ID and sorted values for hashing
    const keyBuffer = new TextEncoder().encode(`${metadataGlossaryId}|${sortedValues.join('|')}`);
    // Generate SHA-1 hash
    const hashBuffer = await crypto.subtle.digest('SHA-1', keyBuffer);
    // Convert hash bytes to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return { key: hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''), values: sortedValues };
  }
}

/**
 * Singleton instance for metadata glossary lookup option validation.
 *
 * This instance is configured to call the validate-lookup-options API endpoint
 * and is used by multiSelectInVivoValidator to deduplicate concurrent validation
 * requests across all spreadsheet cells.
 *
 * ## Configuration
 * - **API Endpoint**: `GET /api/v1/metadata-glossary/{metadataGlossaryId}/validate-lookup-options`
 * - **Debounce Window**: 50ms (batches near-concurrent requests)
 * - **Error Handling**: Errors are caught and return `{ valid: false }` instead of throwing
 * - **Toast Suppression**: API errors don't show toast notifications (onError: false)
 *
 * ## Usage
 * This singleton is imported by multiSelectInVivoValidator and automatically
 * deduplicates validation requests across all instances of the validator.
 *
 * @example
 * ```typescript
 * // Used by multiSelectInVivoValidator
 * const result = await validationCache.validate(
 *   optionsLookupId,
 *   selectedOptionsSet
 * );
 * // Returns: { valid: true } or { valid: false, message: string }
 * ```
 */
export const validationCache = new ValidationRequestCache((metadataGlossaryId, options) => {
  return ApiService.call({
    endpoint: 'GET /api/v1/metadata-glossary/{metadataGlossaryId}/validate-lookup-options',
    path: { metadataGlossaryId },
    query: { option: options },
    options: { onError: { capture: false, throw: false, toast: false, slug: 'lookup-option-validator' } },
  })
    .then((response) => {
      if (response.type === 'success') {
        return response.body.valid ? { valid: true } : { valid: false, message: 'Invalid options' };
      }
      return { valid: false, message: 'Could not verify' };
    })
    .catch(() => ({ valid: false, message: 'Could not verify' })) as Promise<InVivoValidatorResult>;
});
